import {
  getBirdviewInsightsKpiTiles,
  getBirdviewInsightsOpenedClosed,
  getBirdviewInsightsRequestsAging,
  getBirdviewInsightsRequestsByBuilding,
  getBirdviewInsightsRequestsByCategory,
  getBirdviewInsightsRequestsByManager,
  getBirdviewInsightsRequestsByStatus,
  getInhabitantsStatistic,
} from '../../../../api'
import { useInitData } from '../../../../hooks'
import { BirdviewSection } from '../../DashboardBirdview.constants'
import {
  BIRDVIEW_CHART_BUILDING_BARS_DEFAULT_LIMIT,
  BIRDVIEW_CHART_CATEGORY_BARS_DEFAULT_LIMIT,
  BIRDVIEW_CHART_DEFAULT_PERIOD,
  BIRDVIEW_CHART_MANAGER_BARS_DEFAULT_LIMIT,
  BirdviewAgingBasis,
  BirdviewChartMetric,
} from '../../DashboardBirdview.constants.controls'
import { BIRDVIEW_PERSONAL_KPIS_MOCK } from '../../DashboardBirdview.mock'
import { checkIsBirdviewInhabitantsViewable } from '../../DashboardBirdview.utils'
import { checkIsBirdviewPersonalPreset } from '../../DashboardBirdview.utils.charts'

import {
  toBirdviewAgingSection,
  toBirdviewCustomersSection,
  toBirdviewFlowSection,
  toBirdviewStatusSection,
} from './useBirdviewAnalyticsLoaders.utils'
import {
  toBirdviewBuildingSection,
  toBirdviewCategorySection,
  toBirdviewManagerSection,
} from './useBirdviewAnalyticsLoaders.utils.bars'
import {
  toBirdviewKpiFixtures,
  toBirdviewKpis,
} from './useBirdviewAnalyticsLoaders.utils.kpis'

export function useBirdviewAnalyticsLoaders() {
  const { sections: sectionsPermissions } = useInitData()

  // TODO The personal KPIs have no route yet, so they stay on the fixture.
  async function getKpis({
    period = BIRDVIEW_CHART_DEFAULT_PERIOD.value,
    preset,
  }) {
    if (checkIsBirdviewPersonalPreset(preset)) {
      return toBirdviewKpiFixtures(BIRDVIEW_PERSONAL_KPIS_MOCK)
    }

    const { data } = await getBirdviewInsightsKpiTiles({ period })

    return toBirdviewKpis(data)
  }

  async function getOpenedVsClosedSection({
    period = BIRDVIEW_CHART_DEFAULT_PERIOD.value,
  }) {
    const { data } = await getBirdviewInsightsOpenedClosed({ period })

    return toBirdviewFlowSection(data)
  }

  async function getRequestsByStatusSection({
    period = BIRDVIEW_CHART_DEFAULT_PERIOD.value,
  }) {
    const { data } = await getBirdviewInsightsRequestsByStatus({ period })

    return toBirdviewStatusSection(data)
  }

  async function getRequestsByBuildingSection({
    limit = BIRDVIEW_CHART_BUILDING_BARS_DEFAULT_LIMIT,
    metric = BirdviewChartMetric.Count,
    period = BIRDVIEW_CHART_DEFAULT_PERIOD.value,
  }) {
    const { data } = await getBirdviewInsightsRequestsByBuilding({
      limit,
      metric,
      period,
    })

    return toBirdviewBuildingSection(data)
  }

  async function getRequestsAgingSection({
    metric: basis = BirdviewAgingBasis.Created,
  }) {
    const { data } = await getBirdviewInsightsRequestsAging()

    return toBirdviewAgingSection({ basis, data })
  }

  async function getRequestsByCategorySection({
    limit = BIRDVIEW_CHART_CATEGORY_BARS_DEFAULT_LIMIT,
    period = BIRDVIEW_CHART_DEFAULT_PERIOD.value,
  }) {
    const { data } = await getBirdviewInsightsRequestsByCategory({
      limit,
      period,
    })

    return toBirdviewCategorySection(data)
  }

  async function getRequestsByManagerSection({
    limit = BIRDVIEW_CHART_MANAGER_BARS_DEFAULT_LIMIT,
    metric = BirdviewChartMetric.Count,
    period = BIRDVIEW_CHART_DEFAULT_PERIOD.value,
  }) {
    const { data } = await getBirdviewInsightsRequestsByManager({
      limit,
      metric,
      period,
    })

    return toBirdviewManagerSection(data)
  }

  async function getCustomersSection() {
    const { data } = await getInhabitantsStatistic()

    return toBirdviewCustomersSection(data)
  }

  return {
    getters: {
      [BirdviewSection.ChartAging]: getRequestsAgingSection,
      [BirdviewSection.ChartByBuilding]: getRequestsByBuildingSection,
      [BirdviewSection.ChartByManager]: getRequestsByManagerSection,
      [BirdviewSection.ChartByStatus]: getRequestsByStatusSection,
      [BirdviewSection.ChartByType]: getRequestsByCategorySection,
      [BirdviewSection.ChartOpenedVsClosed]: getOpenedVsClosedSection,
      ...(checkIsBirdviewInhabitantsViewable(sectionsPermissions)
        ? { [BirdviewSection.ChartCustomers]: getCustomersSection }
        : null),
      [BirdviewSection.Kpis]: getKpis,
    },
  }
}
